import { secureRandom } from '@/lib/secureRandom';
import { useState, useEffect } from 'react';
import { useBillStore } from '../../../store/billStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Input } from '../../../components/Input';
import toast from 'react-hot-toast';

export function VendorBills() {
    const { bills, isLoading, fetchBills, createBill } = useBillStore();
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchBills();
    }, []);

    const filteredBills = bills.filter(b => 
        b.billNumber.toLowerCase().includes(search.toLowerCase()) || 
        (b.vendorName && b.vendorName.toLowerCase().includes(search.toLowerCase()))
    );

    const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

    const handleCreateMock = async () => {
        try {
            await createBill({
                billNumber: `BILL-${Date.now().toString().slice(-5)}`,
                vendorId: '00000000-0000-0000-0000-000000000000', // Dummy UUID until a vendor select is implemented
                amount: Math.floor(secureRandom() * 2000) + 100,
                taxAmount: 25,
                totalAmount: 225,
                dueDate: new Date().toISOString(),
                receivedDate: new Date().toISOString(),
            });
            toast.success('Bill created');
        } catch (e) {
            // The backend might fail if foreign key constraint on vendor fails, so just show error
            toast.error('Failed to create bill. (Make sure a vendor exists first)');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Vendor Bills</h1>
                    <p className="text-muted-foreground">Manage vendor invoices and accounts payable.</p>
                </div>
                <Button variant="primary" onClick={handleCreateMock}>
                    <Plus className="w-4 h-4 mr-2" />
                    Record Bill
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Draft Bills</p>
                            <h3 className="text-foreground font-display text-metric">
                                {bills.filter(o => o.status === 'draft').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                            <AlertCircle className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Pending Approval</p>
                            <h3 className="text-foreground font-display text-metric">
                                {bills.filter(o => o.status === 'pending').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <CheckCircle className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Paid</p>
                            <h3 className="text-foreground font-display text-metric">
                                {bills.filter(o => o.status === 'paid').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Bills</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input 
                                placeholder="Search bills..." 
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Bill Number</th>
                                    <th className="px-6 py-3">Vendor</th>
                                    <th className="px-6 py-3">Amount</th>
                                    <th className="px-6 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredBills.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">
                                            No bills found.
                                        </td>
                                    </tr>
                                ) : filteredBills.map(op => (
                                    <tr key={op.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">{op.billNumber}</td>
                                        <td className="px-6 py-4">{op.vendorName || 'Unknown Vendor'}</td>
                                        <td className="px-6 py-4">{formatCurrency(op.totalAmount)}</td>
                                        <td className="px-6 py-4">
                                            <Badge variant={op.status === 'paid' ? 'success' : op.status === 'draft' ? 'secondary' : 'primary'}>
                                                {op.status.toUpperCase()}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
